import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, Activity, Zap, Dumbbell, Scale, Percent,
  Plus, Edit2, Trash2, X, Save, Youtube, Video, FileText, ChevronDown, ChevronUp,
} from "lucide-react";
import AppShell from "../../components/AppShell";
import { PlayerTestsHub } from "../../components/testlib/PlayerTestsHub";
import { api, formatErr } from "../../lib/api";
import Sparkline from "../../components/Sparkline";

const TYPE_META = {
  vertical_jump: { label: "Salto vertical",      icon: Zap,      color: "#A855F7", unit: "cm" },
  sprint_10m:    { label: "Sprint 10m",          icon: Activity, color: "#F97316", unit: "s" },
  sprint_30m:    { label: "Sprint 30m",          icon: Activity, color: "#F97316", unit: "s" },
  squat_1rm:     { label: "Squat 1RM",           icon: Dumbbell, color: "#3B82F6", unit: "kg" },
  bench_1rm:     { label: "Press banca 1RM",     icon: Dumbbell, color: "#3B82F6", unit: "kg" },
  weight:        { label: "Peso corporal",       icon: Scale,    color: "#22D3EE", unit: "kg" },
  body_fat:      { label: "% Grasa",             icon: Percent,  color: "#EAB308", unit: "%" },
};

const LOWER_IS_BETTER = new Set(["sprint_10m", "sprint_30m", "body_fat"]);

const ALL_TYPES = [
  { value: "vertical_jump", label: "Salto vertical", unit: "cm" },
  { value: "sprint_10m", label: "Sprint 10m", unit: "s" },
  { value: "sprint_30m", label: "Sprint 30m", unit: "s" },
  { value: "squat_1rm", label: "Squat 1RM", unit: "kg" },
  { value: "bench_1rm", label: "Press banca 1RM", unit: "kg" },
  { value: "weight", label: "Peso corporal", unit: "kg" },
  { value: "body_fat", label: "% Grasa", unit: "%" },
  { value: "custom", label: "Personalizado", unit: "" },
];

const EMPTY = {
  test_type: "vertical_jump", label: "", value: "", unit: "cm", date: "",
  notes: "", client_video_url: "",
};

export default function ClientTests() {
  const [tests, setTests] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [expanded, setExpanded] = useState({});

  const [assigned, setAssigned] = useState(null);
  const load = async () => {
    const [{ data }, asg] = await Promise.all([api.get("/me/tests"), api.get("/me/test-assignments").catch(() => ({ data: { assignments: [], legacy_migrated_ids: [] } }))]);
    const migrated = new Set(asg.data.legacy_migrated_ids || []);
    setAssigned(asg.data);
    setTests(data.filter((t) => !migrated.has(t.id)));
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const t of tests) {
      const key = t.test_type === "custom" ? `custom_${t.label}` : t.test_type;
      (g[key] ||= []).push(t);
    }
    return g;
  }, [tests]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY });
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      test_type: t.test_type,
      label: t.label || "",
      value: t.value != null ? String(t.value) : "",
      unit: t.unit || "",
      date: t.date ? new Date(t.date).toISOString().slice(0, 10) : "",
      notes: t.notes || "",
      client_video_url: t.client_video_url || "",
    });
    setOpen(true);
  };

  const onTypeChange = (v) => {
    const t = ALL_TYPES.find((tt) => tt.value === v);
    setForm((f) => ({ ...f, test_type: v, unit: t?.unit ?? f.unit }));
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        test_type: form.test_type,
        label: form.label || null,
        value: form.value === "" ? null : parseFloat(form.value),
        unit: form.unit || "",
        date: form.date ? new Date(form.date).toISOString() : null,
        notes: form.notes || null,
        client_video_url: form.client_video_url || null,
      };
      if (editing) {
        await api.put(`/me/tests/${editing.id}`, payload);
        toast.success("Test actualizado");
      } else {
        await api.post("/me/tests", payload);
        toast.success("Test guardado");
      }
      setOpen(false); setEditing(null);
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const removeOwn = async (t) => {
    if (!confirm("¿Eliminar tu entrada?")) return;
    try {
      await api.delete(`/me/tests/${t.id}`);
      toast.success("Eliminado"); load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <AppShell>
      <div className="qv-page-head" data-testid="client-tests">
        <div className="label">Tests y métricas</div>
        <h1>Tu evolución</h1>
        <p className="sub">Tus resultados de tests de rendimiento. Comprueba tu progreso a lo largo del tiempo.</p>
      </div>

      <PlayerTestsHub assigned={assigned} reload={load} />
      {tests.length > 0 && <div style={{ color: "#737373", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 700, margin: "6px 0 10px" }}>Historial anterior</div>}

      {tests.length === 0 ? (
        <div className="qv-empty">
          Aún no hay resultados anteriores registrados.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(310px, 1fr))", gap: 14 }}>
          {Object.entries(grouped).map(([key, items]) => {
            const meta = TYPE_META[key] || { label: items[0].label || key, icon: Activity, color: "#a3a3a3" };
            // Latest = items[0] (sorted desc by backend)
            const latest = items[0];
            const previous = items[1];
            const lower = LOWER_IS_BETTER.has(key);
            const numericItems = items.filter((i) => i.value != null);
            const delta = previous && latest.value != null && previous.value != null ? +(latest.value - previous.value).toFixed(2) : null;
            const improved = delta !== null && (lower ? delta < 0 : delta > 0);
            const TrendIcon = delta == null ? null : (improved ? TrendingUp : TrendingDown);
            const trendColor = improved ? "#22C55E" : "#E11D2E";

            // Coach instructions/tutorial: pick the first non-empty from any entry
            const coachDesc = items.find((i) => i.description)?.description;
            const coachVid = items.find((i) => i.tutorial_url)?.tutorial_url;
            const isExpanded = !!expanded[key];

            return (
              <div key={key} className="qv-card-app" data-testid={`test-card-${key}`} style={{ borderColor: `${meta.color}33` }}>
                {/* Header */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 34, height: 34, background: `${meta.color}18`, border: `1px solid ${meta.color}55`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <meta.icon size={16} color={meta.color} />
                    </div>
                    <div>
                      <div style={{ fontFamily: "Bebas Neue", fontSize: "1.15rem", color: "#fff", letterSpacing: "0.04em" }}>{meta.label}</div>
                      <div style={{ fontSize: 10, color: "#737373", letterSpacing: "0.18em", textTransform: "uppercase" }}>{items.length} registros</div>
                    </div>
                  </div>
                </div>

                {/* Big number */}
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6 }}>
                  {latest.value != null ? (
                    <>
                      <div style={{ fontFamily: "Bebas Neue", fontSize: "3rem", color: meta.color, lineHeight: 1 }}>{latest.value}</div>
                      <div style={{ color: "#a3a3a3", fontSize: 14 }}>{latest.unit}</div>
                    </>
                  ) : (
                    <div style={{ fontFamily: "Bebas Neue", fontSize: "1.6rem", color: "#737373", lineHeight: 1 }}>No medible</div>
                  )}
                  {delta !== null && (
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 3, color: trendColor, fontSize: 12, fontWeight: 700, padding: "2px 8px", background: improved ? "rgba(34,197,94,0.08)" : "rgba(225,29,46,0.08)", border: `1px solid ${trendColor}55` }}>
                      {TrendIcon && <TrendIcon size={12} />}
                      {delta > 0 ? "+" : ""}{delta}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 11, color: "#737373", textTransform: "uppercase", letterSpacing: "0.18em" }}>
                  Último · {new Date(latest.date).toLocaleDateString("es-ES")}
                </div>

                {/* Coach instructions */}
                {(coachDesc || coachVid) && (
                  <div style={{ marginTop: 12, padding: 10, background: "rgba(225,29,46,0.04)", borderLeft: "2px solid rgba(225,29,46,0.4)" }}>
                    <div style={{ fontSize: 9, color: "var(--qv-red)", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 700, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                      <FileText size={11} /> Cómo hacer este test
                    </div>
                    {coachDesc && <div style={{ fontSize: 12, color: "#cfcfcf", whiteSpace: "pre-wrap", marginBottom: coachVid ? 8 : 0 }}>{coachDesc}</div>}
                    {coachVid && (
                      <a href={coachVid} target="_blank" rel="noreferrer" className="qv-btn-ghost" style={{ fontSize: 10, marginTop: 4 }}>
                        <Youtube size={12} color="#E11D2E" /> VER DEMOSTRACIÓN
                      </a>
                    )}
                  </div>
                )}

                {/* Sparkline */}
                {numericItems.length >= 2 && (
                  <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                    <Sparkline values={[...numericItems].reverse().map((i) => i.value)} color={meta.color} height={50} />
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#737373", marginTop: 6, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                      <span>Mejor: <b style={{ color: meta.color }}>{lower ? Math.min(...numericItems.map((n) => n.value)) : Math.max(...numericItems.map((n) => n.value))} {latest.unit}</b></span>
                      <span>Primera: <b style={{ color: "#cfcfcf" }}>{numericItems[numericItems.length - 1].value}</b></span>
                    </div>
                  </div>
                )}

                {/* History toggle */}
                {items.length >= 1 && (
                  <button
                    onClick={() => setExpanded((e) => ({ ...e, [key]: !e[key] }))}
                    style={{ background: "transparent", border: "none", color: "#737373", fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 700, cursor: "pointer", padding: "8px 0", display: "inline-flex", alignItems: "center", gap: 4, marginTop: 8 }}
                    data-testid={`expand-${key}`}
                  >
                    {isExpanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />} {isExpanded ? "Ocultar histórico" : `Ver histórico (${items.length})`}
                  </button>
                )}

                {isExpanded && (
                  <div style={{ marginTop: 4, display: "flex", flexDirection: "column", gap: 6 }} data-testid={`history-${key}`}>
                    {items.map((t) => (
                      <div key={t.id} style={{ padding: 10, background: "#060606", border: "1px solid rgba(255,255,255,0.05)" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                          <div style={{ fontSize: 11, color: "#737373", letterSpacing: "0.12em", textTransform: "uppercase" }}>
                            {new Date(t.date).toLocaleDateString("es-ES")}
                            {t.created_by === "client" && <span style={{ marginLeft: 6, color: "#22C55E", fontWeight: 700 }}>· Atleta</span>}
                          </div>
                          <div style={{ fontFamily: "Bebas Neue", fontSize: "1.15rem", color: meta.color, lineHeight: 1 }}>
                            {t.value != null ? <>{t.value}<span style={{ fontFamily: "Inter", fontSize: 10, color: "#a3a3a3", marginLeft: 3 }}>{t.unit}</span></> : <span style={{ color: "#737373", fontSize: "0.85rem" }}>No medible</span>}
                          </div>
                        </div>
                        {t.notes && <div style={{ fontSize: 12, color: "#cfcfcf", marginTop: 4, fontStyle: "italic" }}>"{t.notes}"</div>}
                        <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>
                          {t.client_video_url && (
                            <a href={t.client_video_url} target="_blank" rel="noreferrer" className="qv-btn-ghost" style={{ fontSize: 10, borderColor: "rgba(34,197,94,0.4)", color: "#22C55E" }}>
                              <Video size={11} /> VER MI VÍDEO
                            </a>
                          )}
                          <button onClick={() => openEdit(t)} className="qv-btn-ghost" style={{ fontSize: 10 }} data-testid={`edit-${t.id}`}>
                            <Edit2 size={11} /> EDITAR
                          </button>
                          {t.created_by === "client" && (
                            <button onClick={() => removeOwn(t)} className="qv-btn-ghost" style={{ fontSize: 10, color: "var(--qv-red)", borderColor: "rgba(225,29,46,0.4)" }}>
                              <Trash2 size={11} /> ELIMINAR
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <div className="qv-modal-bg" onClick={() => setOpen(false)} data-testid="test-modal">
          <form className="qv-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit} style={{ maxWidth: 560 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
              <h3 style={{ fontFamily: "Bebas Neue", fontSize: "1.8rem" }}>{editing ? "EDITAR TEST" : "APUNTAR TEST"}</h3>
              <button type="button" onClick={() => setOpen(false)} style={{ background: "transparent", border: "none", color: "#737373", cursor: "pointer" }}><X size={20} /></button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div className="qv-field" style={{ gridColumn: "1/-1" }}>
                <label>Tipo</label>
                <select value={form.test_type} onChange={(e) => onTypeChange(e.target.value)} disabled={!!editing && editing.created_by === "admin"}>
                  {ALL_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              {form.test_type === "custom" && (
                <div className="qv-field" style={{ gridColumn: "1/-1" }}>
                  <label>Nombre del test</label>
                  <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Ej: T-Test, Cooper..." disabled={!!editing && editing.created_by === "admin"} />
                </div>
              )}

              <div className="qv-field">
                <label>Valor (vacío = no medible)</label>
                <input type="number" step="0.01" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder="Ej: 45" data-testid="my-value" />
              </div>
              <div className="qv-field">
                <label>Unidad</label>
                <input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="cm, kg, s, %" />
              </div>
              <div className="qv-field" style={{ gridColumn: "1/-1" }}>
                <label>Fecha (vacío = hoy)</label>
                <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <div className="qv-field" style={{ gridColumn: "1/-1" }}>
                <label>Notas (sensaciones, condiciones...)</label>
                <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Cómo te has sentido, condiciones del día..." style={{ height: "auto", padding: "10px 12px" }} />
              </div>
              <div className="qv-field" style={{ gridColumn: "1/-1" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, color: "#22C55E" }}>
                  <Video size={11} /> URL del vídeo (cómo lo has hecho)
                </label>
                <input value={form.client_video_url} onChange={(e) => setForm({ ...form, client_video_url: e.target.value })} placeholder="https://... (YouTube, Drive, iCloud)" data-testid="my-video-url" />
                <div style={{ fontSize: 10, color: "#737373", marginTop: 4 }}>
                  Sube el vídeo a YouTube/Drive/iCloud y pega aquí el enlace para compartirlo con tu coach.
                </div>
              </div>
            </div>

            <button type="submit" className="qv-btn-primary" style={{ width: "100%", justifyContent: "center", marginTop: 18 }} data-testid="my-test-submit">
              <Save size={14} /> {editing ? "ACTUALIZAR" : "GUARDAR TEST"}
            </button>
          </form>
        </div>
      )}
    </AppShell>
  );
}
